import _ from 'lodash';
window._ = _;

// 根据当前域名自动推断环境和 API 地址
// 例如：qa-eureka.zhihuiya.com -> qa, eureka.zhihuiya.com -> prod
const getEnvFromHostname = () => {
    const hostname = window.location.hostname;
    // 匹配 qa-eureka, stage-eureka, eureka 等
    const match = hostname.match(/^(qa-|stage-)?eureka\.zhihuiya\.com$/);
    if (match) {
        const prefix = match[1] || ''; // 'qa-', 'stage-', 或 ''
        return prefix.replace(/-$/, ''); // 'qa', 'stage', 或 ''
    }
    return 'stage'; // 默认 stage
};

const getServiceUrl = (serviceName: string) => {
    const env = getEnvFromHostname();
    const prefix = env ? `${env}-` : '';
    return `https://${prefix}${serviceName}.zhihuiya.com`;
};

// @ts-ignore fix for @patsnap/common-ps-http
// 优先级：
// 1. index.html 设置的值（由部署流程替换占位符）
// 2. Vite 环境变量（开发环境）
// 3. 默认值（根据域名自动推断）
const getEnvValue = (viteKey: string, defaultValue: string, windowEnvKey?: string) => {
    // 1. 首先检查 window.process.env 是否已有值（来自 index.html，部署时替换）
    const existingValue = windowEnvKey && (window as any).process?.env?.[windowEnvKey];
    if (existingValue &&
        existingValue !== '' &&
        existingValue !== 'undefined' &&
        !existingValue.includes('{{') &&
        !existingValue.includes('${')) {
        return existingValue;
    }

    // 2. 然后检查 Vite 环境变量（开发环境）
    const viteValue = (import.meta.env as any)[viteKey];
    if (viteValue !== undefined &&
        viteValue !== '' &&
        viteValue !== 'undefined' &&
        viteValue !== '__RUNTIME__' &&
        !viteValue.includes('${') &&
        !viteValue.includes('{{')) {
        return viteValue;
    }

    // 3. 否则使用默认值（基于域名推断）
    return defaultValue;
};

const env = getEnvFromHostname();
const prefix = env ? `${env}-` : '';

(window as any).process = {
    ...(window as any).process,
    env: {
        VUE_APP_DEPLOY_ENV: getEnvValue('VITE_DEPLOY_ENV', env || 'production', 'VUE_APP_DEPLOY_ENV'),
        VUE_APP_PASSPORT_CLIENT_ID: getEnvValue('VITE_PASSPORT_CLIENT_ID', '4355e565135c41b3b7395cbaf94f1b67', 'VUE_APP_PASSPORT_CLIENT_ID'),
        VUE_APP_PASSPORT_CLIENT_NAME: getEnvValue('VITE_PASSPORT_CLIENT_NAME', 'eureka', 'VUE_APP_PASSPORT_CLIENT_NAME'),
        VUE_APP_ACCOUNT_SERVICE_URI: getEnvValue('VITE_ACCOUNT_SERVICE_URI', `https://${prefix}account-service.zhihuiya.com`, 'VUE_APP_ACCOUNT_SERVICE_URI'),
        VUE_APP_PASSPORT_URI: getEnvValue('VITE_PASSPORT_URI', `https://${prefix}passport.zhihuiya.com`, 'VUE_APP_PASSPORT_URI'),
        VUE_APP_EUREKA_SERVICE_URI: getEnvValue('VITE_EUREKA_SERVICE_URI', `https://${prefix}eureka-service.zhihuiya.com`, 'VUE_APP_EUREKA_SERVICE_URI'),
        VUE_APP_CORE_BASIC_SERVICE_URI: getEnvValue('VITE_CORE_BASIC_SERVICE_URI', `https://${prefix}basic-service.zhihuiya.com`, 'VUE_APP_CORE_BASIC_SERVICE_URI'),
        VUE_APP_STATIC_COMMON_URL: getEnvValue('VITE_STATIC_COMMON_URL', `https://${prefix}static-common.zhihuiya.com`, 'VUE_APP_STATIC_COMMON_URL'),
        VUE_APP_GEETEST_MESSAGEAUTHENTICATION: getEnvValue('VITE_GEETEST_MESSAGEAUTHENTICATION', '93a93689c223c9f52de553e31fb60241'),
        VUE_APP_EUREKA_SOCKET_URI: getEnvValue('VITE_EUREKA_SOCKET_URI', `wss://${prefix}eureka-service.zhihuiya.com`, 'VUE_APP_EUREKA_SOCKET_URI'),
        VUE_APP_WORKSPACE_SERVICE_URI: getEnvValue('VITE_WORKSPACE_SERVICE_URI', `https://${prefix}workspace-service.zhihuiya.com`, 'VUE_APP_WORKSPACE_SERVICE_URI'),
        VUE_APP_EUREKA_URI: getEnvValue('VITE_EUREKA_URI', `https://${prefix}eureka.zhihuiya.com`, 'VUE_APP_EUREKA_URI'),
        // Vault 后端 API 地址
        VUE_APP_VAULT_API_URI: getEnvValue('VITE_VAULT_API_URI', `http://${prefix}s-data-govern-knowledge.patsnap.info`, 'VUE_APP_VAULT_API_URI'),
    },
};

// 本地开发环境：拦截所有对 zhihuiya.com 的请求，重定向到 Vite 代理
if (import.meta.env.DEV && window.location.hostname === 'localhost') {

    // 获取本地存储的 token
    const getLocalToken = () => localStorage.getItem('authToken') || localStorage.getItem('access_token');

    // 本地开发默认代理到 stage 环境
    const proxyMap: Record<string, string> = {
        'account.zhihuiya.com': '/proxy-account',
        'account-service.zhihuiya.com': '/proxy-account-service',
        'passport.zhihuiya.com': '/proxy-passport',
        'eureka-service.zhihuiya.com': '/proxy-eureka-service',
        'basic-service.zhihuiya.com': '/proxy-basic-service',
        'workspace-service.zhihuiya.com': '/proxy-workspace-service',
        'eureka.zhihuiya.com': '/proxy-eureka',
        'static-common.zhihuiya.com': '/proxy-static-common',
    };

    // 拦截 fetch
    const originalFetch = window.fetch;
    window.fetch = function(input: RequestInfo | URL, init?: RequestInit) {
        let url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

        // 检查是否需要代理
        for (const [domain, proxyPath] of Object.entries(proxyMap)) {
            const patterns = [
                `https://${domain}`,
                `https://qa-${domain}`,
                `https://stage-${domain}`,
                `http://${domain}`,
                `http://qa-${domain}`,
                `http://stage-${domain}`,
            ];

            for (const pattern of patterns) {
                if (url.startsWith(pattern)) {
                    url = url.replace(pattern, proxyPath);
                    break;
                }
            }
        }

        // 本地开发：如果有 token，添加 Authorization header
        const token = getLocalToken();
        if (token && url.startsWith('/proxy-')) {
            init = init || {};
            init.headers = new Headers(init.headers || {});
            if (!init.headers.has('Authorization')) {
                init.headers.set('Authorization', `Bearer ${token}`);
            }
        }

        return originalFetch(url, init);
    };

    // 拦截 XMLHttpRequest
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;
    const originalSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

    XMLHttpRequest.prototype.open = function(method: string, url: string | URL, ...args: any[]) {
        let urlStr = typeof url === 'string' ? url : url.href;

        // 检查是否需要代理
        for (const [domain, proxyPath] of Object.entries(proxyMap)) {
            const patterns = [
                `https://${domain}`,
                `https://qa-${domain}`,
                `https://stage-${domain}`,
                `http://${domain}`,
                `http://qa-${domain}`,
                `http://stage-${domain}`,
            ];

            for (const pattern of patterns) {
                if (urlStr.startsWith(pattern)) {
                    urlStr = urlStr.replace(pattern, proxyPath);
                    break;
                }
            }
        }

        // 标记这个请求是否需要添加 Authorization
        (this as any)._needsAuth = urlStr.startsWith('/proxy-');
        (this as any)._hasAuth = false;

        return originalOpen.call(this, method, urlStr, ...args);
    };

    XMLHttpRequest.prototype.setRequestHeader = function(name: string, value: string) {
        if (name.toLowerCase() === 'authorization') {
            (this as any)._hasAuth = true;
        }
        return originalSetRequestHeader.call(this, name, value);
    };

    XMLHttpRequest.prototype.send = function(body?: Document | XMLHttpRequestBodyInit | null) {
        // 本地开发：如果需要认证且没有 Authorization header，添加它
        const token = getLocalToken();
        if (token && (this as any)._needsAuth && !(this as any)._hasAuth) {
            originalSetRequestHeader.call(this, 'Authorization', `Bearer ${token}`);
        }
        return originalSend.call(this, body);
    };
}
